import { Popover, PopoverContent, PopoverTrigger, Portal } from '@chakra-ui/react';
import { Button, Panel, Tabs } from '@sketchnote/shared-components';
import cn from 'classnames';
import * as React from 'react';
import { API, endpoints } from 'src/api';
import { useDocServices } from 'src/api/services/documents';

import styles from './CoverImage.module.css';

const { useState, useCallback } = React;

export interface CoverImageProps {
  coverImage: string;
  onChangeCover?: (img: string) => void;
  onDisableCover?: () => void;
  disabled?: boolean;
  gallery?: {
    title: string;
    images: {
      src: string;
      thumbnail?: string;
    }[];
  }[];
}

type recentImg = {
  src: string;
};

export const CoverImage = ({
  coverImage,
  onChangeCover,
  onDisableCover,
  disabled = false,
}: CoverImageProps) => {
  const [isChangeCoverOpened, setIsChangeCoverOpened] = useState(false);
  const [recentUploads, setRecentUploads] = useState<recentImg[]>([]);
  const [gallery, setgallery] = useState<any>();
  const { getRecentUploads, postRecentUpload } = useDocServices();

  const hiddenFileInput = React.useRef<any>(null);
  const imgUrlRef = React.useRef<any>(null);

  const toggleMenu = (value?: boolean) => () => {
    setIsChangeCoverOpened(value ?? !isChangeCoverOpened);
  };

  const handleChooseImg = useCallback(() => {
    hiddenFileInput.current.click();
  }, []);

  const handleFileUpload = async (file) => {
    const data = new FormData();

    data.append('file', file);
    const response = await API.post(endpoints.creationFile.url, data, {
      headers: { ['Content-Type']: 'multipart/form-data' },
    });

    return response.data?.data?.message?.url;
  };

  const onFileChange = useCallback(async (event) => {
    if (event.target.files.length) {
      const imgUrl = await handleFileUpload(event.target.files[0]);
      setIsChangeCoverOpened(false);
      onChangeCover?.(imgUrl);
      await postRecentUpload(imgUrl);
    }
  }, []);

  const handleButtonClick = async () => {
    const images: recentImg[] = await getRecentUploads();
    setRecentUploads(images);
    const importedJsonData = await import('src/constants/coverImages.json');
    setgallery(importedJsonData?.default);
    //  setgallery(JSON.parse(JSON.stringify(importedJsonData))?.default);
  };

  const handleLink = () => {
    setIsChangeCoverOpened(false);
    onChangeCover?.(imgUrlRef.current.value);
  };

  return (
    <div className={cn('w-full relative px-8 pt-8', styles.cover_image)} style={{ height: '23vh' }}>
      {!disabled && (
        <div
          className={cn(
            'absolute bottom-24 right-24 rounded-sm bg-white flex px-12 py-4',
            { 'opacity-0': !isChangeCoverOpened },
            styles.toolbar
          )}
        >
          <Popover
            placement="bottom"
            isOpen={isChangeCoverOpened}
            onOpen={toggleMenu(true)}
            onClose={toggleMenu(false)}
            modifiers={[{ name: 'eventListeners', options: { scroll: isChangeCoverOpened } }]}
          >
            <PopoverTrigger>
              <button
                disabled={disabled}
                className="flex items-center text-sm text-gray-800"
                onClick={handleButtonClick}
              >
                Change cover
              </button>
            </PopoverTrigger>
            <Portal>
              <PopoverContent>
                <div
                  style={{ maxHeight: '70vh' }}
                  className="flex overflow-auto flex-col p-16 rounded-lg bg-white shadow-sm text-gray-999 w-420 relative"
                >
                  <Tabs
                    selected={0}
                    size="small"
                    className="border-b font-regular text-xs items-center mr-24"
                  >
                    <Panel title="Gallery">
                      <div className="my-16">
                        {gallery?.map((item, index) => (
                          <React.Fragment key={`${item.title}-${index}`}>
                            <div className="text-sm text-gray-600 my-10">{item.title}</div>
                            <div className={cn('flex flex-wrap')}>
                              {item.images.map((image, index) => (
                                <button
                                  className={cn('w-86 h-54', {
                                    'mr-8 mb-8': (index + 1) % 4 !== 0,
                                  })}
                                  onClick={() => {
                                    setIsChangeCoverOpened(false);
                                    onChangeCover?.(image.src);
                                  }}
                                  key={`${image.src}-${index}`}
                                >
                                  <img
                                    src={image.thumbnail || image.src}
                                    loading="lazy"
                                    className="w-full h-full object-center object-cover rounded-md"
                                  />
                                </button>
                              ))}
                            </div>
                          </React.Fragment>
                        ))}
                      </div>
                    </Panel>
                    <Panel title="Upload">
                      <div className="mt-16">
                        <Button
                          status="primary"
                          className="mb-8 w-368 justify-center pr-10 pl-10"
                          size="sm"
                          onClick={handleChooseImg}
                        >
                          Choose image
                        </Button>
                        <input
                          ref={hiddenFileInput}
                          accept="image/*"
                          className="hidden"
                          id="file"
                          multiple={false}
                          type="file"
                          onChange={onFileChange}
                        />
                        <div className="text-sm text-gray-600 mb-26">
                          Images wider than 1500 pixels work best
                        </div>
                        <div className="text-lg font-regular text-gray-600 my-10 mb-14">
                          Recent uploads
                        </div>
                        <div className="my-16">
                          {recentUploads.map((image, index) => (
                            <button
                              className={cn('w-86 h-54', {
                                'mr-8 mb-8': (index + 1) % 4 !== 0,
                              })}
                              onClick={() => {
                                setIsChangeCoverOpened(false);
                                onChangeCover?.(image.src);
                              }}
                              key={`${image}-${index}`}
                            >
                              <img
                                src={image.src}
                                loading="lazy"
                                className="w-full h-full object-center object-cover rounded-md"
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    </Panel>
                    <Panel title="Link">
                      <div className="mt-16">
                        <input
                          placeholder="http://"
                          className="py-14 bg-gray-200 rounded-sm h-48 w-full text-gray-500 font-regular placeholder-gray-500"
                          ref={imgUrlRef}
                        />
                        <Button
                          status="primary"
                          className="ml-256 mt-16"
                          size="sm"
                          onClick={handleLink}
                        >
                          Embed image
                        </Button>
                      </div>
                    </Panel>
                  </Tabs>
                  <button
                    className="absolute rounded-md right-20 px-6 py-3 hover:bg-gray-100 font-regular text-sm items-center text-gray-500"
                    onClick={() => {
                      setIsChangeCoverOpened(false);
                      onDisableCover?.();
                    }}
                  >
                    Remove
                  </button>
                </div>
              </PopoverContent>
            </Portal>
          </Popover>
        </div>
      )}
      <img
        className="w-full object-center object-cover rounded-xl h-full"
        loading="lazy"
        src={coverImage}
      />
    </div>
  );
};
